import {  Vector3, TextureLoader, SpriteMaterial, Sprite, Group, Raycaster, Vector2} from "three";
import { ScenesManager } from "../ScenesManager";
import gsap from "gsap";
import { isDesktop, isMobile } from "utils/platform";
import { CameraManager } from "../CameraManager";

//const EYE_ICON = "assets/textures/eye-sprite.svg"
const EYE_ICON = "assets/textures/eye-sprite-outline.svg"
//const UNLOCK_ICON = "assets/textures/unlock-sprite.svg"
const UNLOCK_ICON = "assets/textures/unlock-sprite-outline.svg"

const SPRITE_SIZE = 0.1

interface IEyeState {
    [key: string] : {
        visible: boolean,
        type: "interior" | "exterior",
        tl: gsap.core.Timeline
    }
}

export enum SpriteType {
    EYE,
    UNLOCK
}


export class SpriteManager extends Group{
    spriteMaterial: SpriteMaterial = new SpriteMaterial({transparent: true,depthTest: false, depthWrite: false, color: "white"});
    eyeState: IEyeState = {};
    raycaster: Raycaster = new Raycaster()
    vec2Helper: Vector2 = new Vector2()
    pulseTimeline = gsap.timeline()
    static inInterior = false;
    cameraThresholdDistance = 3;

    setup(){
        ScenesManager.mainScene.scene.add(this)
        
        new TextureLoader().load(EYE_ICON, (texture) =>{
            this.spriteMaterial.map = texture
            this.spriteMaterial.map.needsUpdate = true
        });

        document.addEventListener("click", (ev) =>{
            if (ScenesManager.inFeature) {
                document.body.style.cursor = "auto"
                return;
            };

            const intersects = ScenesManager.raycaster.intersectObjects(
              this.children
            );

            if (intersects.length > 0) {
              const rayTarget = intersects[0].object;
              if (rayTarget && rayTarget.userData.featureName) {
                const target =
                  ScenesManager.EXTERNAL_FEATURES_DATA[rayTarget.userData.featureName];
                const targetInterior =
                  ScenesManager.INTERNAL_FEATURES_DATA[rayTarget.userData.featureName];
                  if ((target && !SpriteManager.inInterior) || (targetInterior && SpriteManager.inInterior)) {
                    const coordinates = !SpriteManager.inInterior ? target.coordinates : targetInterior.coordinates;
                    const targetCoords = isMobile() ? coordinates.mobile : coordinates.desktop;
                    if (targetCoords) {
                    ScenesManager.currentFeatureName = rayTarget.userData.featureName;
                    
                    let boxTarget = rayTarget
                    if (isDesktop())
                    {
                        const featureHits = ScenesManager.inInterior ? ScenesManager.assets.featureHitsInterior.scene : ScenesManager.assets.featureHits.scene;
                        let boxIndicator = featureHits.clone().getObjectByName(`${rayTarget.userData.featureName}`)
                        
                        if (boxIndicator !== undefined) {
                            boxIndicator!.position.y -= 0.85
                            boxTarget = boxIndicator
                        }
                    }
                    else if (isMobile())
                    {
                        let boxIndicator = ScenesManager.assets.mobileBoxes.scene.clone().getObjectByName(`mobile_box_${rayTarget.userData.featureName}`)             
                        
                        if (boxIndicator != undefined) {
                            boxIndicator!.position.y -= 0.85
                            boxTarget = boxIndicator
                        }
                    }

                    CameraManager.goToFeature(targetCoords.pos, targetCoords.lookAt, boxTarget);
                  }
                }
              }
            }
        }, {passive: false})

        document.addEventListener("mousemove", () =>{
            if (ScenesManager.inFeature) {
                document.body.style.cursor = "auto"
                return;
            };

            const visibleChildren = this.children.filter((child) =>{
                return this.eyeState[child.name].visible;
            })

            const intersects = ScenesManager.raycaster.intersectObjects(
                visibleChildren
            );

            if (intersects.length > 0) {
                document.body.style.cursor = "pointer"
            }
            else{
                document.body.style.cursor = "auto"
            }
        })



    }

    add_sprite_at(name: string, type: "interior" | "exterior", position: Vector3, sprite_type: SpriteType) {
        if (this.getObjectByName(name))
        {
            return;
        }
        
        let material = new SpriteMaterial({transparent: true,depthTest: false, depthWrite: false, color: "white"})
        
        switch (sprite_type) {
            case SpriteType.EYE:
                new TextureLoader().load(EYE_ICON, (texture) =>{
                    material.map = texture
                    material.map.needsUpdate = true
                });
                break;
            case SpriteType.UNLOCK:
                new TextureLoader().load(UNLOCK_ICON, (texture) =>{
                    material.map = texture
                    material.map.needsUpdate = true
                });
                break;
            default:
                break;
        }

        const sprite = new Sprite(material)
        sprite.material.transparent = true
        sprite.renderOrder = 999
        sprite.name = name
        this.eyeState[name] = {
            visible: false, 
            type,
            tl: gsap.timeline()
        }
        sprite.position.copy(position)
        sprite.position.y -= (0.8 + SPRITE_SIZE/2)
        sprite.scale.set(0, 0, 0);

        sprite.userData.featureName = name

        gsap.fromTo(sprite.material, {
            opacity: 0.5,
        },
        {
            opacity: 1.3,
            yoyo: true,
            repeat: -1,
            repeatDelay: 1,
            ease: "Power2.easeInOut",
            duration: 1,
            delay: this.children.length * 0.5
        })

        this.add(sprite)
    }  

    hideSprite(name: string, duration?: number, delay?: number) {
        //check if sprite needs to be hidden
        const sprite = this.getObjectByName(name);
        if (!sprite) return;
        if (!this.eyeState[name].visible) return;

        this.eyeState[name].tl.kill()
        this.eyeState[name].tl = gsap.timeline()

        this.eyeState[name].tl.to(sprite.scale, {
            x: 0,
            y: 0,
            z: 0,
            ease: "power2.out",
            duration: duration? duration : 0.5,
            delay: delay? delay : 0.0
        })
        this.eyeState[name].visible = false
    }

    showSprite(name: string, duration?: number, delay?: number) {
        //check if sprite needs to be shown
        const sprite = this.getObjectByName(name);
        if (!sprite) return;
        if (this.eyeState[name].visible) return;

        this.eyeState[name].tl.kill()
        this.eyeState[name].tl = gsap.timeline()

        this.eyeState[name].tl.to(sprite.scale, {
            x: SPRITE_SIZE,
            y: SPRITE_SIZE,
            z: 1,
            ease: "power2.out",
            duration: duration? duration : 0.5,
            delay: delay? delay : 0.0
        })

        this.eyeState[name].visible = true
    }

    update() {
        //on each frame, check if sprites are still visible
        this.children.forEach((child) =>{

            const spriteDistance = child.position.distanceTo(ScenesManager.mainScene.camera.position)

            if (ScenesManager.inFeature || !ScenesManager.startedExperience || CameraManager.playingIntro) {
                this.hideSprite(child.name)
            }
            else if (SpriteManager.inInterior && this.eyeState[child.name].type === "exterior")
            {
                //if a child is exterior, hide it when on interior
                this.hideSprite(child.name)
            }
            else if (!SpriteManager.inInterior && this.eyeState[child.name].type === "interior")
            {
                //if a child is interior, hide it when on exterior
                this.hideSprite(child.name, 0.01)
            }
            else if (SpriteManager.inInterior && this.eyeState[child.name].type === "interior")
            {
                //always show sprites on the interior, provided they are not too close
                if (spriteDistance > this.cameraThresholdDistance)
                {
                    this.showSprite(child.name, 0.5, 0.5)
                }
                else
                {
                    this.hideSprite(child.name)
                }
            }
            else
            {
                const screenPosition = child.position.clone()
                screenPosition.y += SPRITE_SIZE/2;
                screenPosition.project(ScenesManager.mainScene.camera)
                this.vec2Helper.set(screenPosition.x, screenPosition.y)
    
                this.raycaster.setFromCamera(this.vec2Helper, ScenesManager.mainScene.camera)
                const intersects = this.raycaster.intersectObjects([...ScenesManager.featureHits])
                
                
                if (intersects.length === 0){
                    this.showSprite(child.name)
                }
                else
                {
                    const carIntersectionDistance = intersects[0].distance
    
                    if (spriteDistance < carIntersectionDistance)
                    {
                        this.showSprite(child.name)
                    }
                    else
                    {
                        this.hideSprite(child.name)
                    }
                }
            }
        })

        //for visible sprites




    }

}